import { Fragment, FunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import { DraftConfiguration, PrintType } from '@/types';
import { t } from '@/utils/i18n';
import { getCanSwitchPrintType } from '@/redux/draft/getters/getCanSwitchPrintType';
import {
  PrintTypeSelector,
  needsDeactivatedPrintTypeHint,
} from '@/components/common/PrintTypeSelector';
import { CaretRight } from '@/components/common/icons/Caret';
import { SubPanel } from '@/components/common/panel/panel';

interface PrintTypeSelectorButtonProps {
  printType: PrintType;
  onClick: () => void;
}

export const PrintTypeSelectorButton: FunctionComponent<PrintTypeSelectorButtonProps> = ({
  printType,
  onClick,
}) => {
  const canSwitchPrintType = useSelector(getCanSwitchPrintType(printType));
  const showHintForDeactivatedPrintType = needsDeactivatedPrintTypeHint(printType);

  const printTypeLabel = (
    <Fragment>
      <span>{t('general.printType')}</span>
      <span
        className={cn('print-type-selector__current-name', {
          'print-type-selector__current-name--deactivated': showHintForDeactivatedPrintType,
        })}
      >
        {printType.name}
        {showHintForDeactivatedPrintType ? ' - deactivated' : ''}
      </span>
    </Fragment>
  );

  return (
    <div className="print-type-selector">
      {canSwitchPrintType ? (
        <button
          className="print-type-selector__current print-type-selector__current--active"
          onClick={onClick}
          type="button"
        >
          {printTypeLabel}
          <CaretRight />
        </button>
      ) : (
        <div className="print-type-selector__current">{printTypeLabel}</div>
      )}
    </div>
  );
};

interface PrintTypePanelProps {
  active: boolean;
  parent: string;
  configuration: DraftConfiguration;
  onBackClick: () => void;
}

export const PrintTypePanel: FunctionComponent<PrintTypePanelProps> = ({
  active,
  parent,
  configuration,
  onBackClick,
}) => (
  <SubPanel active={active} headline={t('general.printType')} parent={parent} onBack={onBackClick}>
    <PrintTypeSelector configuration={configuration} onClose={onBackClick} />
  </SubPanel>
);
